import React, { useState, useEffect, useRef, useCallback } from 'react';
import EmailEditorComponent from 'react-email-editor';
import { emailTemplatesAPI } from '../services/api';
import { Save, FolderOpen, Trash2, Eye, Code, Palette, Type, X } from 'lucide-react';
import toast from 'react-hot-toast';


export default function EmailEditor({ value, onChange, subject, onSubjectChange, channel, onDesignChange, initialDesign, botUsers = false, botMedia = false }) {
  const editorRef = useRef(null);
  const hasDesignLoaded = useRef(false);
  const [mode, setMode] = useState('builder');
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [editorReady, setEditorReady] = useState(false);
  const [htmlCode, setHtmlCode] = useState(value || '');

  useEffect(() => {
    if (channel === 'email') {
      emailTemplatesAPI.list().then(r => setTemplates(r.data || [])).catch(() => {});
    }
  }, [channel]);

  const [pendingHtmlDesign, setPendingHtmlDesign] = useState(null);

  const onReady = useCallback(() => {
    setEditorReady(true);

    if (editorRef.current?.editor) {
      editorRef.current.editor.setLocale('ru-RU');
    }

    if (initialDesign) {
      try {
        const design = typeof initialDesign === 'string' ? JSON.parse(initialDesign) : initialDesign;
        editorRef.current?.editor?.loadDesign(design);
        hasDesignLoaded.current = true;
      } catch (e) {
        console.warn('Failed to load design:', e);
      }
    } else if (pendingHtmlDesign && editorRef.current?.editor) {
      editorRef.current.editor.loadDesign({
        body: {
          rows: [{
            cells: [1],
            columns: [{
              contents: [{
                type: 'html',
                values: { html: pendingHtmlDesign }
              }]
            }]
          }]
        }
      });
      hasDesignLoaded.current = true;
      setPendingHtmlDesign(null);
    }
  }, [initialDesign, pendingHtmlDesign]);

  const exportHtml = useCallback(() => {
    return new Promise((resolve) => {
      if (!editorRef.current?.editor) { resolve(value); return; }
      editorRef.current.editor.exportHtml((data) => {
        const { html, design } = data;
        resolve({ html, design });
      });
    });
  }, [value]);


  const syncFromBuilder = useCallback(async () => {
    if (editorReady && editorRef.current?.editor) {
      const result = await exportHtml();
      if (result?.html) {
        onChange(result.html);
        setHtmlCode(result.html);
        if (onDesignChange && result.design) {
          onDesignChange(JSON.stringify(result.design));
        }
      }
    }
  }, [editorReady, exportHtml, onChange, onDesignChange]);

  const handleSaveTemplate = async () => {
    if (!saveName.trim()) { toast.error('Введите название'); return; }
    try {
      let html = value;
      let designJson = null;

      if (editorReady && editorRef.current?.editor) {
        const result = await exportHtml();
        if (result?.html) {
          html = result.html;
          designJson = result.design ? JSON.stringify(result.design) : null;
        }
      }

      await emailTemplatesAPI.create({
        name: saveName.trim(),
        subject: subject || '',
        html_content: html,
        design_json: designJson,
      });
      toast.success('Шаблон сохранён');
      setSaveName(''); setShowSave(false);
      emailTemplatesAPI.list().then(r => setTemplates(r.data || []));
    } catch { toast.error('Ошибка сохранения'); }
  };

  const handleLoadTemplate = (t) => {
    onChange(t.html_content);
    setHtmlCode(t.html_content);
    if (t.subject && onSubjectChange) onSubjectChange(t.subject);

    const loadIntoEditor = () => {
      if (t.design_json) {
        try {
          const design = typeof t.design_json === 'string' ? JSON.parse(t.design_json) : t.design_json;
          editorRef.current.editor.loadDesign(design);
          hasDesignLoaded.current = true;
        } catch { }
      } else if (t.html_content) {
        editorRef.current.editor.loadDesign({
          body: {
            rows: [{
              cells: [1],
              columns: [{
                contents: [{
                  type: 'html',
                  values: { html: t.html_content }
                }]
              }]
            }]
          }
        });
        hasDesignLoaded.current = true;
      }
    };

    if (editorRef.current?.editor) {
      loadIntoEditor();
      setMode('builder');
    } else {
      setPendingHtmlDesign(t.html_content);
      setMode('builder');
    }

    setShowTemplates(false);
    toast.success(`Шаблон «${t.name}» загружен`);
  };

  const handleDeleteTemplate = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Удалить шаблон?')) return;
    try {
      await emailTemplatesAPI.delete(id);
      toast.success('Удалено');
      setTemplates(templates.filter(t => t.id !== id));
    } catch { toast.error('Ошибка'); }
  };

  if (channel !== 'email') {
    return (
      <div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, lineHeight: 1.6 }}>
          Переменные: <code>{'{name}'}</code> {!botUsers&&<><code>{'{phone}'}</code> <code>{'{balance}'}</code> <code>{'{tier}'}</code></>}{botUsers&&(botMedia?' · короткая подпись к файлу':' · до 3500 символов')}
        </div>
        <textarea className="form-input" maxLength={botUsers?(botMedia?800:3500):undefined} rows={5} value={value} onChange={e => onChange(e.target.value)}
          placeholder={channel === "bots" ? "Введите текст сообщения бота..." : "Введите текст SMS..."} style={{ resize: 'vertical' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="form-group" style={{ marginBottom: 8 }}>
        <label className="form-label">Тема письма</label>
        <input className="form-input" value={subject || ''} onChange={e => onSubjectChange?.(e.target.value)}
          placeholder="Специальное предложение для вас!" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <button type="button" onClick={() => {
              syncFromBuilder();
              if (editorRef.current?.editor && htmlCode && !hasDesignLoaded.current) {
                editorRef.current.editor.loadDesign({
                  body: {
                    rows: [{
                      cells: [1],
                      columns: [{
                        contents: [{
                          type: 'html',
                          values: { html: htmlCode }
                        }]
                      }]
                    }]
                  }
                });
                hasDesignLoaded.current = true;
              }
              setMode('builder');
            }}
            style={{ padding: '6px 12px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: mode === 'builder' ? '#8b5cf6' : '#fff', color: mode === 'builder' ? '#fff' : '#374151' }}>
            <Palette size={12} /> Конструктор
          </button>
          <button type="button" onClick={() => { syncFromBuilder(); setMode('html'); }}
            style={{ padding: '6px 12px', border: 'none', borderLeft: '1px solid #e5e7eb', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: mode === 'html' ? '#8b5cf6' : '#fff', color: mode === 'html' ? '#fff' : '#374151' }}>
            <Code size={12} /> HTML
          </button>
          <button type="button" onClick={() => { syncFromBuilder(); setMode('preview'); }}
            style={{ padding: '6px 12px', border: 'none', borderLeft: '1px solid #e5e7eb', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: mode === 'preview' ? '#8b5cf6' : '#fff', color: mode === 'preview' ? '#fff' : '#374151' }}>
            <Eye size={12} /> Превью
          </button>
        </div>

        <div style={{ flex: 1 }} />

        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowTemplates(!showTemplates)}
          style={{ fontSize: 11 }}>
          Выбрать шаблон {templates.length > 0 && `(${templates.length})`}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowSave(!showSave)}
          style={{ fontSize: 11 }}>
          <Save size={12} /> Сохранить
        </button>
      </div>

      <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 8 }}>
        Переменные: <code>{'{name}'}</code> <code>{'{phone}'}</code> <code>{'{email}'}</code> <code>{'{balance}'}</code> <code>{'{tier}'}</code>
      </div>

      {showTemplates && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 8, marginBottom: 10, maxHeight: 280, overflowY: 'auto' }}>
          {templates.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 12, padding: 12, textAlign: 'center' }}>Нет сохранённых шаблонов</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              {templates.map(t => (
                <div key={t.id} style={{
                  border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                }}
                  className="hover-row" onClick={() => handleLoadTemplate(t)}>
                  <div style={{ height: 120, overflow: 'hidden', background: '#f9fafb', position: 'relative' }}>
                    <iframe
                      srcDoc={t.html_content}
                      style={{ width: '200%', height: '240px', transform: 'scale(0.5)', transformOrigin: '0 0', border: 'none', pointerEvents: 'none' }}
                      title={t.name}
                    />
                  </div>
                  <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>{t.name}</div>
                      {t.subject && <div style={{ fontSize: 10, color: '#9ca3af' }}>{t.subject}</div>}
                    </div>
                    <button onClick={e => handleDeleteTemplate(t.id, e)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', padding: 2 }}
                      onMouseOver={e => e.target.style.color = '#ef4444'}
                      onMouseOut={e => e.target.style.color = '#d1d5db'}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showSave && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', padding: 10,
          background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
          <input className="form-input" style={{ flex: 1, fontSize: 12 }} placeholder="Название шаблона..."
            value={saveName} onChange={e => setSaveName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSaveTemplate()} />
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveTemplate}>Сохранить</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowSave(false)}>✕</button>
        </div>
      )}

      <div style={{ display: mode === 'builder' ? 'block' : 'none', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <EmailEditorComponent
            ref={editorRef}
            onReady={onReady}
            minHeight={500}
            projectId={286550}
            options={{
              locale: 'ru-RU',
              features: { textEditor: { tables: true } },
              mergeTags: [
                { name: 'Имя', value: '{name}' },
                { name: 'Телефон', value: '{phone}' },
                { name: 'Email', value: '{email}' },
                { name: 'Баланс бонусов', value: '{balance}' },
                { name: 'Уровень', value: '{tier}' },
                { name: 'Пол', value: '{gender}' },
              ],
              appearance: { theme: 'light' },
            }}
          />
        </div>

      {mode === 'html' && (
        <textarea className="form-input" rows={14} value={htmlCode}
          onChange={e => { setHtmlCode(e.target.value); onChange(e.target.value); }}
          placeholder="<html><body><h1>Привет, {name}!</h1></body></html>"
          style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5 }} />
      )}

      {mode === 'preview' && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
          <iframe
            srcDoc={value || htmlCode}
            style={{ width: '100%', minHeight: 500, border: 'none' }}
            title="Preview"
          />
        </div>
      )}
    </div>
  );
}